import React from 'react';
import SummaryCard from './SummaryCard';
import styles from './RoomsHeaderSummary.module.scss';

import type { RoomsOperatingSummary } from 'model/RoomsOperatingReport';
import { Spinner } from 'components/UI';

type RoomsHeaderSummaryProps = {
    data?: RoomsOperatingSummary | null;
    loading?: boolean;
    className?: string;
};

const fmtCurrency = (n: number) =>
    n.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const fmtPct = (p: number) => `${(p * 100).toFixed(0)}%`;

const RoomsHeaderSummary: React.FC<RoomsHeaderSummaryProps> = ({ data, loading = false, className }) => {
    if (loading) {
        return (
            <div className={`${styles.container} ${className ?? ''}`}>
                <div className={styles.loading}>
                    <Spinner className={styles.spinner} />
                </div>
            </div>
        );
    }

    if (!data) return null;

    const items = [
        {
            key: 'roomsAvailable',
            title: 'Rooms Available',
            value: data.roomsAvailable.toLocaleString()
        },
        {
            key: 'roomsOccupied',
            title: 'Rooms Occupied',
            value: (
                <>
                    {data.roomsOccupied.value.toLocaleString()} ({fmtPct(data.roomsOccupied.pct)})
                </>
            )
        },
        {
            key: 'vacantRooms',
            title: 'Vacant Rooms',
            value: (
                <>
                    {data.vacantRooms.value.toLocaleString()} ({fmtPct(data.vacantRooms.pct)})
                </>
            )
        },
        { key: 'petsServed', title: 'Pets Served', value: data.petsServed.toLocaleString() },
        { key: 'adr', title: 'Average Daily Rate (ADR)', value: fmtCurrency(data.adr) },
        { key: 'revpar', title: 'RevPAR', value: fmtCurrency(data.revpar) },
        { key: 'totalRoomRevenue', title: 'Total Room Revenue', value: fmtCurrency(data.totalRoomRevenue) }
    ] as const;

    return (
        <div className={`${styles.container} ${className ?? ''}`}>
            {items.map(it => (
                <SummaryCard
                    key={it.key}
                    title={it.title}
                    value={it.value}
                    data-testid={`summary-${it.key}`}
                />
            ))}
        </div>
    );
};

export default RoomsHeaderSummary;
